import { useMemo, useState } from 'react';
import {
  CalendarClock,
  Hourglass,
  Inbox,
  PauseCircle,
  Sprout,
  type LucideIcon,
} from 'lucide-react';
import {
  partitionDashboardContacts,
  type DashboardCounts,
  type DashboardLane,
} from '@/lib/crm/dashboard';
import {
  selectDueScheduledContacts,
  useDueScheduledContacts,
  useLaneContacts,
  useTodayQueue,
  type LaneContactFilters,
} from '@/hooks/crm/useContacts';
import type { CrmFilters } from '@/hooks/crm/useCrmFilters';
import { SectionHeader } from './SectionHeader';
import { ContactCard } from './ContactCard';
import { Pagination } from './Pagination';
import { selectDueScheduledQueueContacts, selectTodayQueueContacts } from './TodayQueue';

// Mirrors `DEFAULT_LANE_PAGE_SIZE` in `src/hooks/crm/useContacts.ts`. That
// module is an import-only dependency for this feature (see the task's
// module boundaries), so the constant can't be exported from there without
// touching a file outside this change's scope.
const LANE_PAGE_SIZE = 20;

interface LaneConfig {
  lane: DashboardLane;
  id: string;
  title: string;
  icon: LucideIcon;
  storageKey: string;
  defaultCollapsed?: boolean;
}

const LANE_CONFIGS: LaneConfig[] = [
  { lane: 'my-turn', id: 'my-turn-section', title: 'MY TURN', icon: Inbox, storageKey: 'crm-my-turn-collapsed' },
  { lane: 'their-turn', id: 'their-turn-section', title: 'THEIR TURN', icon: Hourglass, storageKey: 'crm-their-turn-collapsed' },
  { lane: 'scheduled', id: 'scheduled-section', title: 'SCHEDULED', icon: CalendarClock, storageKey: 'crm-scheduled-collapsed' },
  { lane: 'nurture', id: 'nurture-section', title: 'NURTURE', icon: Sprout, storageKey: 'crm-nurture-collapsed', defaultCollapsed: true },
  { lane: 'on-hold', id: 'on-hold-section', title: 'ON HOLD', icon: PauseCircle, storageKey: 'crm-on-hold-collapsed', defaultCollapsed: true },
];

interface DashboardSectionsProps {
  filters: CrmFilters;
  counts: DashboardCounts;
  /**
   * The current lane/due filter's true total, from the index/count query
   * (see `resolveVisibleTotal` in `index.tsx`) — decides whether ANY section
   * has something to show at all.
   */
  visibleTotal: number;
  onPageChange: (page: number) => void;
  /**
   * The SAME "now" snapshot `index.tsx` passes to `TodayQueue` — required,
   * not computed here, so this component's `useDueScheduledContacts` call
   * can never disagree with `TodayQueue`'s about which local day's data to
   * fetch/classify. See `TodayQueueProps.now`'s doc comment for the race
   * this closes.
   */
  now: Date;
  /**
   * The SAME `dueScheduledExpanded` boolean `TodayQueue.tsx` uses to decide
   * how much of the due-scheduled list it renders — required, not tracked
   * independently here, so this component's SCHEDULED-lane exclusion set
   * (`dueScheduledShownIds`, derived via the same
   * `selectDueScheduledQueueContacts`) can never disagree with what
   * `TodayQueue` is actually showing. See
   * `TodayQueueProps.dueScheduledExpanded`'s doc comment for the drift this
   * closes.
   */
  dueScheduledExpanded: boolean;
}

/**
 * Which already-shown-elsewhere contact ids a given lane's grid should
 * drop, so a person deduped from Today's my-turn band or its due-scheduled
 * block never renders twice on the same screen. A named helper rather than
 * a nested ternary (`src/CLAUDE.md` forbids nested ternary chains).
 */
function exclusionIdsForLane(
  lane: DashboardLane,
  todayShownContactIds: Set<string>,
  dueScheduledShownIds: Set<string>,
): Set<string> | undefined {
  if (lane === 'my-turn') return todayShownContactIds;
  if (lane === 'scheduled') return dueScheduledShownIds;
  return undefined;
}

/**
 * The lane's empty-page copy, once every fetched row has been excluded
 * (already shown elsewhere on screen). Whether "see other pages" is a true
 * statement depends on whether another page actually exists — reuses the
 * EXACT condition the pagination control itself renders on
 * (`totalCount > LANE_PAGE_SIZE || page > 1`), so the two can never
 * disagree. A named helper rather than a nested ternary (`src/CLAUDE.md`
 * forbids nested ternary chains, max one level) — DIFF Round 4, F5.
 */
function emptyLaneMessage(contactsLength: number, totalCount: number, page: number): string {
  if (contactsLength === 0) return 'No contacts match the current filters.';
  const hasOtherPages = totalCount > LANE_PAGE_SIZE || page > 1;
  return hasOtherPages
    ? 'All contacts on this page are already shown above — see other pages.'
    : 'All contacts are already shown above.';
}

function readCollapsedPreference(storageKey: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;

  try {
    const saved = window.localStorage.getItem(storageKey);
    return saved === null ? fallback : saved === 'true';
  } catch {
    return fallback;
  }
}

interface LaneSectionProps {
  config: LaneConfig;
  filters: LaneContactFilters;
  totalCount: number;
  onPageChange: (page: number) => void;
  /**
   * Contact ids already visible in the Today band above (see
   * `DashboardSections`) — populated for the two lanes `TodayQueue` draws
   * from: `my-turn` (its due-today rows) and `scheduled` (its due-scheduled
   * block). Rendering these again here would show the same person twice on
   * one screen, so they're dropped from the grid. `totalCount`/`pageCount`
   * stay untouched by this — the lane's real size is never silently
   * shrunk, only what's drawn in the grid is deduped.
   */
  excludeContactIds?: Set<string>;
}

function LaneSection({
  config,
  filters,
  totalCount,
  onPageChange,
  excludeContactIds,
}: LaneSectionProps): JSX.Element {
  const { lane, id, title, icon: Icon, storageKey, defaultCollapsed = false } = config;
  const [isCollapsed, setIsCollapsed] = useState(() =>
    readCollapsedPreference(storageKey, defaultCollapsed));
  const headingId = `${id}-heading`;
  const contentId = `${id}-content`;

  const handleToggle = (): void => {
    setIsCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        // Storage is optional; collapse remains functional in-memory.
      }
      return next;
    });
  };

  // Collapsed lanes fetch nothing at all — that's the concrete fix for "the
  // app tires me out": NURTURE/ON HOLD (and any other collapsed lane) still
  // show a correct count via `totalCount`, without ever loading their rows.
  const { data: contacts = [], isLoading, isError } = useLaneContacts(lane, filters, {
    enabled: !isCollapsed,
  });

  // Purely a rendering concern over the page we already fetched — dedupes
  // against the Today band, never against lane/page membership itself (see
  // the `excludeContactIds` doc comment above).
  const visibleContacts = useMemo(() => {
    if (!excludeContactIds || excludeContactIds.size === 0) return contacts;
    return contacts.filter((contact) => !excludeContactIds.has(contact.id));
  }, [contacts, excludeContactIds]);

  // Membership/counts stay index-authoritative (see dashboard.ts); this is
  // purely a rendering concern over the page we already fetched, asking
  // "which of these already-loaded my-turn rows should get the red
  // overdue border" — not recomputing which lane/page they belong to.
  const overdueIds = useMemo(() => {
    if (lane !== 'my-turn' || visibleContacts.length === 0) return null;
    const { overdue } = partitionDashboardContacts(visibleContacts, new Date(), {
      preserveOrder: true,
    }).myTurn;
    return new Set(overdue.map((contact) => contact.id));
  }, [lane, visibleContacts]);

  return (
    <section id={id} aria-labelledby={headingId}>
      <SectionHeader
        title={title}
        icon={Icon}
        totalCount={totalCount}
        pageCount={visibleContacts.length}
        headingId={headingId}
        controlsId={contentId}
        isCollapsed={isCollapsed}
        onToggle={handleToggle}
      />
      <div id={contentId} hidden={isCollapsed}>
        {!isCollapsed && isLoading && (
          <p
            className="py-6 text-center text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            Loading {title.toLowerCase()}...
          </p>
        )}

        {!isCollapsed && !isLoading && isError && (
          <p className="py-6 text-center text-sm text-destructive" role="alert">
            {title} contacts could not be loaded.
          </p>
        )}

        {!isCollapsed && !isLoading && !isError && visibleContacts.length === 0 && (
          <p
            className="py-6 text-center text-sm text-muted-foreground"
            role="status"
          >
            {emptyLaneMessage(contacts.length, totalCount, filters.page)}
          </p>
        )}

        {!isCollapsed && !isLoading && !isError && visibleContacts.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {visibleContacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                isOverdue={overdueIds?.has(contact.id) ?? false}
              />
            ))}
          </div>
        )}

        {/*
          Rendered independent of `visibleContacts.length` — the exclusion
          filter above (`excludeContactIds`) can empty an entire fetched page
          (e.g. every SCHEDULED row on page 1 is also shown in Today's
          expanded due-scheduled block) while later pages still hold
          reachable contacts. Gating this on `visibleContacts.length > 0`
          would remove the only control that can reach those pages — see F1
          in the Independent Review disposition table.
        */}
        {!isCollapsed && !isLoading && !isError && (totalCount > LANE_PAGE_SIZE || filters.page > 1) && (
          <div className="mt-4">
            <Pagination
              page={filters.page}
              pageSize={LANE_PAGE_SIZE}
              totalCount={totalCount}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>
    </section>
  );
}

export function DashboardSections({
  filters,
  counts,
  visibleTotal,
  onPageChange,
  now,
  dueScheduledExpanded,
}: DashboardSectionsProps): JSX.Element {
  const laneFilters: LaneContactFilters = { q: filters.q, due: filters.due, page: filters.page };
  const isFiltered = filters.due !== null || filters.lane !== null || filters.q !== '';

  // The Today band (`TodayQueue`, rendered above this component in
  // `index.tsx`) draws exclusively from the my-turn lane. Sharing its query
  // here (same react-query key, so this never issues a second network
  // request) lets the my-turn `LaneSection` drop exactly those already-shown
  // contacts from its own grid — otherwise a my-turn contact due today would
  // render twice on the same screen with the same accessible name.
  const { data: todayQueueContacts = [] } = useTodayQueue();
  const todayShownContactIds = useMemo(
    () => new Set(selectTodayQueueContacts(todayQueueContacts).map((contact) => contact.id)),
    [todayQueueContacts],
  );

  // Same dedupe pattern as the my-turn/Today overlap above, for the
  // due-scheduled block (Option B — see the plan's DESIGN IT TWICE):
  // `useDueScheduledContacts` shares its query key with `TodayQueue.tsx`'s
  // own call, so this issues zero extra requests. The exclusion set is
  // derived from EXACTLY what `TodayQueue` is currently rendering — the
  // `MAX_QUEUE_ROWS`-sliced subset when collapsed, or the full
  // fetched-and-filtered list when `dueScheduledExpanded` (the SAME shared
  // boolean, lifted from `index.tsx`) is true. Both surfaces call the same
  // `selectDueScheduledQueueContacts` with the same boolean, so they can
  // never independently disagree about which contacts are visible where.
  const { data: dueScheduledData, isError: dueScheduledIsError } = useDueScheduledContacts(now);
  const dueScheduledContacts = useMemo(
    () => selectDueScheduledContacts({ data: dueScheduledData, isError: dueScheduledIsError }, now),
    [dueScheduledData, dueScheduledIsError, now],
  );
  const dueScheduledShownIds = useMemo(
    () =>
      new Set(
        selectDueScheduledQueueContacts(dueScheduledContacts, dueScheduledExpanded).map(
          (contact) => contact.id,
        ),
      ),
    [dueScheduledContacts, dueScheduledExpanded],
  );

  if (visibleTotal === 0) {
    return (
      <div
        className="rounded-lg border-2 border-dashed border-border bg-surface px-4 py-20 text-center"
        role="status"
      >
        <p className="text-xl text-muted-foreground">
          {isFiltered && counts.hasActiveContacts ? 'No contacts match this filter.' : 'No active contacts.'}
        </p>
        {!counts.hasActiveContacts && (
          <p className="mt-2 text-sm text-text-tertiary">
            Synced and assigned contacts will appear here.
          </p>
        )}
      </div>
    );
  }

  const visibleLaneConfigs = LANE_CONFIGS.filter(
    (config) =>
      (filters.lane === null || filters.lane === config.lane)
      && counts.laneCounts[config.lane] > 0,
  );

  return (
    <div className="space-y-8">
      {visibleLaneConfigs.map((config) => (
        <LaneSection
          key={config.lane}
          config={config}
          filters={laneFilters}
          totalCount={counts.laneCounts[config.lane]}
          onPageChange={onPageChange}
          excludeContactIds={exclusionIdsForLane(config.lane, todayShownContactIds, dueScheduledShownIds)}
        />
      ))}
    </div>
  );
}
